import React from "react";
import {
  ContentLayout,
  CustomeDialog,
  HeaderBreadcrumbs,
  Loader,
  LoadingButton,
  usePermission,
} from "../../../shared";
import { usePromosAndNotificationsDetails } from "../hooks";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteIconBlue from "../../../assets/images/Icons/DeleteIconBlue.png";
import PlayIconBlue from "../../../assets/images/Icons/ic_blue_play.svg";
import {
  DetailsComponent,
  PublishNotificationOrPromoALert,
  ReadUsersList,
} from "../components";

const chipStyle = {
  draft: { backgroundColor: "#1560D414", color: "#1560D4" },
  published: { backgroundColor: "#E1FBF2", color: "#0F875B" },
  unpublished: { backgroundColor: "#0000000F", color: "#A3A3A3" },
  ended: { backgroundColor: "#FFE7E3", color: "#C42B1C" },
};

export const PromosAndNotificationsDetails = () => {
  const {
    state,
    title,
    isNotification,
    breadcrumbLinks,
    PROMO_NOTIFICATION_STATUS,
    dispatch,
    onCloneNotificationOrPromo,
    onDelete,
    onPublishNotificationOrPromo,
    publishOrUnpublishPromosOrNotification,
  } = usePromosAndNotificationsDetails({});
  const navigate = useNavigate();
  const isEditable = usePermission("promo-notification-modify");
  const isDeletable = usePermission("promo-notification-delete");

  const status = state.details?.status;

  const tabs = React.useMemo(() => {
    return isNotification && !state.loading.list
      ? [
          {
            label: "Details",
            value: "1",
            panel: <DetailsComponent state={state} />,
          },
          {
            label: "Read Users",
            value: "2",
            panel: <ReadUsersList data={state.details?.read} />,
          },
        ]
      : [];
  }, [isNotification, state.loading.list]);

  return (
    <ContentLayout
      title={
        state.loading.list ? (
          <Skeleton variant="rounded" width="150px" height="30px" />
        ) : (
          title
        )
      }
      chipType="other"
      statusContent={
        state.loading.list ? (
          <Skeleton variant="rounded" width="88px" height="24px" />
        ) : (
          <Chip
            size="small"
            label={status}
            sx={{
              backgroundColor: chipStyle[status].backgroundColor,
              color: chipStyle[status].color,
            }}
          />
        )
      }
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
      rightContent={
        <Stack direction="row" gap="12px">
          {/* Copy Button */}
          {isEditable && (
            <LoadingIconButton
              onClick={() =>
                dispatch({
                  type: "SET_DIALOG_OPEN",
                  payload: { type: "clone", open: true },
                })
              }
              title="Clone"
              loading={state.loading.cloneButton}
            >
              <img
                src="/icons/ic_copy_blue.svg"
                alt="clone"
                width="20px"
                height="20px"
              />
            </LoadingIconButton>
          )}

          {/* publish button */}
          {status === "published" && isEditable && (
            <LoadingIconButton
              onClick={() =>
                publishOrUnpublishPromosOrNotification({
                  type: PROMO_NOTIFICATION_STATUS.unpublish,
                })
              }
              loading={state.loading.pauseButton}
              title="Unpublish"
            >
              <img
                src="/icons/play_icon.svg"
                alt="publish"
                width="20px"
                height="20px"
              />
            </LoadingIconButton>
          )}

          {/* Unpublish button */}
          {status === "unpublished" && isEditable && (
            <LoadingIconButton
              loading={state.loading.playButton}
              onClick={() =>
                dispatch({
                  type: "SET_DIALOG_OPEN",
                  payload: {
                    type: PROMO_NOTIFICATION_STATUS.publish,
                    open: true,
                  },
                })
              }
              title="Publish"
            >
              <img src={PlayIconBlue} alt="publish" />
            </LoadingIconButton>
          )}

          {/* Delete Button */}
          {isDeletable && (
            <LoadingIconButton
              loading={state.loading.deleteButton}
              onClick={() =>
                dispatch({
                  type: "SET_DIALOG_OPEN",
                  payload: { type: "delete", open: true },
                })
              }
              title="Delete"
            >
              <img src={DeleteIconBlue} alt="delete" />
            </LoadingIconButton>
          )}

          {status === "draft" && isEditable && (
            <>
              <Button variant="text" onClick={() => navigate(`edit`)}>
                Edit
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() =>
                  dispatch({
                    type: "SET_DIALOG_OPEN",
                    payload: {
                      type: PROMO_NOTIFICATION_STATUS.publish,
                      open: true,
                    },
                  })
                }
                loading={state.loading.playButton}
              >
                Publish
              </LoadingButton>
            </>
          )}
        </Stack>
      }
      tabProps={
        isNotification
          ? {
              label: "generalTab", // Label for the tab component
              list: tabs, // List of tabs to display
            }
          : undefined
      }
    >
      {state.loading.list ? (
        <Stack mt="30vh">
          <Loader />
        </Stack>
      ) : (
        !isNotification && <DetailsComponent state={state} />
      )}

      {/* Publish Alert */}
      {isEditable && (
        <>
          <PublishNotificationOrPromoALert
            open={state.isDialogsOpen.published}
            name={state.details?.name}
            status={status}
            type={state?.details?.type}
            onClose={() =>
              dispatch({
                type: "SET_DIALOG_OPEN",
                payload: { type: "published", open: false },
              })
            }
            onPublish={onPublishNotificationOrPromo}
          />

          {/* Clone Alert */}
          <CustomeDialog
            open={state.isDialogsOpen.clone}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              sx: {
                width: "423px",
                p: "16px",
              },
            }}
            title={`Clone ${state?.details?.type}?`}
            content={
              <Box>
                <Typography variant="body1" my="12px">
                  Are you sure you want to create a copy of
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {` "${state.details?.name}"?`}
                  </Box>
                </Typography>
                <Stack direction="row" gap="12px" justifyContent="flex-end">
                  <Button
                    color="info"
                    variant="text"
                    size="small"
                    onClick={() =>
                      dispatch({
                        type: "SET_DIALOG_OPEN",
                        payload: { type: "clone", open: false },
                      })
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onCloneNotificationOrPromo}
                  >
                    Clone
                  </Button>
                </Stack>
              </Box>
            }
          />
          <CustomeDialog
            open={state.isDialogsOpen.delete}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              sx: {
                width: "423px",
                p: "16px",
              },
            }}
            title={`Delete ${state?.details?.type}?`}
            content={
              <Box>
                <Typography variant="body1" pt="12px" pb="16px">
                  Are you sure you want to delete
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {` "${state.details?.name}"?`}
                  </Box>
                </Typography>
                <Stack direction="row" gap="12px" justifyContent="flex-end">
                  <Button
                    color="info"
                    variant="text"
                    size="small"
                    onClick={() =>
                      dispatch({
                        type: "SET_DIALOG_OPEN",
                        payload: { type: "delete", open: false },
                      })
                    }
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" size="small" onClick={onDelete}>
                    Delete
                  </Button>
                </Stack>
              </Box>
            }
          />
        </>
      )}
    </ContentLayout>
  );
};

const LoadingIconButton = ({ loading, children, title, ...rest }) => {
  return (
    <Tooltip title={title} arrow>
      <Button
        disabled={loading}
        sx={{ minWidth: "40px", maxWidth: "40px", padding: 0 }}
        variant="text"
        {...rest}
      >
        {loading ? <CircularProgress size={20} /> : children}
      </Button>
    </Tooltip>
  );
};
